import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { Ban, Edit } from 'lucide-react'; // Icons for edit and block actions
import styled from 'styled-components';
import GenericDropdown from '../../components/GenericDropDown';
import GenericModal from '../../components/GenericModal';
import GenericTable from '../../components/GenericTable';
import { useAuth } from '../../context/AuthContext';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { User } from '../../models/Users';
import { getAllUsers, getUsersByRole, saveUser, toggleUserStatus, updateUser } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';
import AddUserPage from '../AddUserPage';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 2rem;
  gap: 10px;
`;


const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const ManagerPage: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState<User>({} as User);
  const [selectedUser, setSelectedUser] = useState<any>();
  const { role } = useAuth();

  const dispatch = useAppDispatch();
  const {showModalStrip} = useModalStrip();
  const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
  const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);

  useEffect(() => {
    dispatch(getUsersByRole(Role.Manager)); // Fetch users if not already available in Redux
  }, [dispatch]);

  // Generate Dropdown Options for Admins
  let userOptions = users
    .filter((user) => user && user.role === Role.Admin)
    .map((admin) => ({
      value: admin.id.toString(),
      label: admin.name,
    }));

  const handleDelete = (user: User) => {
    dispatch(toggleUserStatus(user.id)).unwrap().then(res => {
      getAllUsers(0);
    }).catch(err => {
      showModalStrip('danger', err.message, 5000);
    });
  };

  const handleOpenModal = (item: any, mode: string, role: string) => {
    setSelectedItem({
      ...item,
      role: role
    });
    if (mode === 'add') {
      setModalShow(true);
    } else {
      if (item.status === 'INACTIVE') {
        return;
      }
      setModalShow(true);
    }
  };

  const handleSaveUser = (user: User) => {
    if (user.id) {
      dispatch(updateUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    } else {
      dispatch(saveUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    }
  }

  const handleUserChange = (adminId: string) => {
    setSelectedUser(adminId);
  };

  return (
    <Container>
      <PageHeading>Managers View</PageHeading>

      <AddButtonContainer>
        {/* Add New User Button */}
        {role === Role.SuperAdmin &&  <Button onClick={() => handleOpenModal({}, 'add', Role.Manager)}>Add Manager</Button>}
      </AddButtonContainer>

      <TableWrapper>
        {/* SuperAdmin selects Admin */}
        {role === Role.SuperAdmin && (
          <GenericDropdown
            label="Select Manager"
            options={userOptions}
            onChange={(id) => handleUserChange(id)}
            value={selectedUser}
          />
        )}

        <GenericTable
          columns={[
            { header: 'ID', field: 'id' },
            { header: 'Name', field: 'name' },
            { header: 'Phone', field: 'mobile' },
            { header: 'Actions', field: 'actions' },
          ]}
          data={users.map((item) => ({
            ...item,
            actions: (
              <ActionButtonContainer>
                <ActionIcon onClick={() => handleOpenModal(item,'edit', Role.Customer)}>
                  <Edit />
                </ActionIcon>
                <ActionIcon onClick={() => handleDelete(item)}>
                  <Ban color={ item.status === 'INACTIVE' ? '#bb2124' : '#0d4a14'}/>
                </ActionIcon>
              </ActionButtonContainer>
            ),
          }))}
          actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
          pagination={true}
        />
      </TableWrapper>

      {modalShow && (
        <GenericModal
          isOpen={modalShow}
          onClose={() => setModalShow(false)}
          node={<AddUserPage data={selectedItem as User} onSave={(user) => handleSaveUser(user)} />}
          label={selectedItem && selectedItem.id ? `Update User - ${selectedItem.role}` : `Add New - ${selectedItem.role}`}
        />
      )}
    </Container>
  );
};

export default ManagerPage;
